import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiClient";
import type {
  DraftCalendarItem,
  DraftMeetingRequest,
  DraftMeetingResponse,
  FiscalYearResponse,
  CalendarSubmissionResponse,
  UpdateShowInGRCRequest,
  BulkUpdateStatusRequest,
  UpdateDraftStatesRequest,
  UpdateSignOffActionRequest,
  BulkDraftPushToSummitRequest,
  BulkActionInValidStatusListResponse
} from "@/types/draftCalendar";

// ─── Query keys ────────────────────────────────────────────────────────────────

export const draftCalendarKeys = {
  all: () => ["draft-calendar"] as const,
  fiscalYears: () => ["draft-calendar", "fiscal-years"] as const,
  list: (fiscalYearId: number, userEmail: string) =>
    ["draft-calendar", "list", fiscalYearId, userEmail] as const,
  meeting: (id: number) => ["draft-calendar", "meeting", id] as const,
  calendarStatus: (fiscalYearId: number) =>
    ["draft-calendar", "calendar-status", fiscalYearId] as const,
  attendeeLevels: () => ["draft-calendar", "attendee-levels"] as const,
  locations: () => ["draft-calendar", "locations"] as const,
  meetingTypes: () => ["draft-calendar", "meeting-types"] as const,
  businesses: () => ["draft-calendar", "businesses"] as const,
  duDeclinedReasons: () => ["draft-calendar", "du-declined-reasons"] as const
};

// ─── Reference data ────────────────────────────────────────────────────────────

export function useFiscalYears() {
  return useQuery({
    queryKey: draftCalendarKeys.fiscalYears(),
    queryFn: () => apiFetch<FiscalYearResponse[]>("DraftCalendar/GetAllFiscalYears"),
    staleTime: 5 * 60_000
  });
}

export function useDraftCalendar(fiscalYearId: number, userEmail: string) {
  return useQuery({
    queryKey: draftCalendarKeys.list(fiscalYearId, userEmail),
    queryFn: () =>
      apiFetch<DraftCalendarItem[]>(
        `DraftCalendar?fiscalYearId=${fiscalYearId}&userEmail=${encodeURIComponent(userEmail)}`
      ),
    enabled: fiscalYearId > 0 && userEmail.length > 0,
    staleTime: 30_000,
    refetchOnMount: "always"
  });
}

export function useDraftMeeting(id: number) {
  return useQuery({
    queryKey: draftCalendarKeys.meeting(id),
    queryFn: () =>
      apiFetch<DraftMeetingResponse>(`DraftCalendar/GetDraftMeetingById/${id}`),
    enabled: id > 0
  });
}

export function useCalendarSubmissionStatus(fiscalYearId: number) {
  return useQuery({
    queryKey: draftCalendarKeys.calendarStatus(fiscalYearId),
    queryFn: () =>
      apiFetch<CalendarSubmissionResponse>(
        `DraftCalendar/CheckCalendarSubmission/${fiscalYearId}`
      ),
    enabled: fiscalYearId > 0,
    staleTime: 30_000
  });
}

/** Business → [BusinessLine] map */
export function useDraftBusinesses() {
  return useQuery({
    queryKey: draftCalendarKeys.businesses(),
    queryFn: () =>
      apiFetch<Record<string, string[]>>("Business/GetBusinessAndBusinessLineTypes"),
    staleTime: 5 * 60_000
  });
}

/** Business → [MeetingType] map */
export function useDraftMeetingTypes() {
  return useQuery({
    queryKey: draftCalendarKeys.meetingTypes(),
    queryFn: () =>
      apiFetch<Record<string, string[]>>("MeetingType/GetMeetingTypesWithCategories"),
    staleTime: 5 * 60_000
  });
}

export function useAttendeeLevels() {
  return useQuery({
    queryKey: draftCalendarKeys.attendeeLevels(),
    queryFn: () => apiFetch<string[]>("AttendeeLevel/GetAllAttendeeLevels"),
    staleTime: 5 * 60_000
  });
}

export function useLocations() {
  return useQuery({
    queryKey: draftCalendarKeys.locations(),
    queryFn: () => apiFetch<string[]>("Locations/GetAllLocations"),
    staleTime: 5 * 60_000
  });
}

export function useDUDeclinedReasons(enabled = true) {
  return useQuery({
    queryKey: draftCalendarKeys.duDeclinedReasons(),
    queryFn: () => apiFetch<string[]>("DraftCalendar/GetDUDeclinedReasons"),
    enabled,
    staleTime: 5 * 60_000
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useSaveDraftMeeting() {
  return useMutation({
    mutationFn: (body: DraftMeetingRequest) =>
      apiFetch<boolean>("DraftCalendar/SaveDraftEvent", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useUpdateShowInGRC() {
  return useMutation({
    mutationFn: (body: UpdateShowInGRCRequest) =>
      apiFetch<number[]>("SoftHolds/UpdateShowInGRC", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useBulkUpdateStatus() {
  return useMutation({
    mutationFn: (body: BulkUpdateStatusRequest) =>
      apiFetch<number>("DraftCalendar/BulkUpdateStatus", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useBulkUpdateStates() {
  return useMutation({
    mutationFn: (body: UpdateDraftStatesRequest) =>
      apiFetch<boolean>("DraftCalendar/BulkUpdateStates", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useUpdateSignOffAction() {
  return useMutation({
    mutationFn: (body: UpdateSignOffActionRequest) =>
      apiFetch<number>("DraftCalendar/UpdateSignOffAction", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useBulkDraftPushToSummit() {
  return useMutation({
    mutationFn: (body: BulkDraftPushToSummitRequest) =>
      apiFetch<BulkActionInValidStatusListResponse>("DraftCalendar/BulkDraftPushToSummit", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}
